/**
 * EligibilityEvaluator (AJIP-16.02.005 · WS8)
 * ---------------------------------------------------------------------
 * Deterministic. Auditable. AI may recommend but NEVER determine
 * (Additional Founder Directive). Business rules govern final eligibility.
 *
 * Returns a frozen decision:
 *   { decision, reason, journeyId, journeyVersion, at, appliedRules, trace }
 */
const DECISIONS = Object.freeze({
  ELIGIBLE: 'ELIGIBLE',
  INELIGIBLE: 'INELIGIBLE',
  BLOCKED: 'BLOCKED',
  UNAVAILABLE: 'UNAVAILABLE',
  DEPRECATED: 'DEPRECATED',
  MISSING_PERMISSION: 'MISSING_PERMISSION',
  FEATURE_DISABLED: 'FEATURE_DISABLED',
  MISSING_PREREQUISITE: 'MISSING_PREREQUISITE',
  CONFLICT_DETECTED: 'CONFLICT_DETECTED',
});

const evaluateEligibility = ({ definition, context, nowFn = () => Date.now() }) => {
  const trace = [];
  const at = new Date(nowFn()).toISOString();
  const rules = [];
  const decide = (decision, reason) => Object.freeze({
    decision,
    reason,
    journeyId: definition.journeyId,
    journeyVersion: definition.journeyVersion,
    at,
    appliedRules: Object.freeze(rules.slice()),
    trace: Object.freeze(trace.slice()),
  });

  // Rule 1 — availability.
  rules.push('availability');
  if (definition.registrationStatus !== 'registered') {
    trace.push({ rule: 'availability', ok: false });
    return decide(DECISIONS.UNAVAILABLE, 'NOT_REGISTERED');
  }
  trace.push({ rule: 'availability', ok: true });

  // Rule 2 — deprecation.
  rules.push('deprecation');
  if (definition.deprecationStatus === 'deprecated' || definition.deprecationStatus === 'retired') {
    trace.push({ rule: 'deprecation', ok: false, status: definition.deprecationStatus });
    return decide(DECISIONS.DEPRECATED, definition.deprecationStatus.toUpperCase());
  }
  trace.push({ rule: 'deprecation', ok: true });

  // Rule 3 — required feature flags.
  rules.push('feature-flags');
  const activeFlags = new Set((context && context.activeFeatureFlags) || []);
  for (const flag of definition.requiredFeatureFlags) {
    if (!activeFlags.has(flag)) {
      trace.push({ rule: 'feature-flags', ok: false, missing: flag });
      return decide(DECISIONS.FEATURE_DISABLED, `FLAG_${flag}_DISABLED`);
    }
  }
  trace.push({ rule: 'feature-flags', ok: true });

  // Rule 4 — permissions.
  rules.push('permissions');
  const perms = new Set((context && context.permissions) || []);
  for (const p of definition.permissionRequirements) {
    if (!perms.has(p)) {
      trace.push({ rule: 'permissions', ok: false, missing: p });
      return decide(DECISIONS.MISSING_PERMISSION, `PERMISSION_${p}_MISSING`);
    }
  }
  trace.push({ rule: 'permissions', ok: true });

  // Rule 5 — audience.
  rules.push('audience');
  if (context && context.audience && !definition.journeyAudience.includes(context.audience)) {
    trace.push({ rule: 'audience', ok: false });
    return decide(DECISIONS.INELIGIBLE, 'AUDIENCE_MISMATCH');
  }
  trace.push({ rule: 'audience', ok: true });

  // Rule 6 — prerequisites (context.completedJourneys).
  rules.push('prerequisites');
  const completed = new Set((context && context.completedJourneys) || []);
  const prereqs = (definition.entryConditions || []).filter((c) => c.startsWith('requires:'));
  for (const req of prereqs) {
    const requiredId = req.slice('requires:'.length);
    if (!completed.has(requiredId)) {
      trace.push({ rule: 'prerequisites', ok: false, missing: requiredId });
      return decide(DECISIONS.MISSING_PREREQUISITE, `PREREQ_${requiredId}_MISSING`);
    }
  }
  trace.push({ rule: 'prerequisites', ok: true });

  // Rule 7 — active-journey conflicts.
  rules.push('conflicts');
  const active = new Set((context && context.activeJourneys) || []);
  const conflicts = (definition.eligibilityRules || []).filter((r) => r.startsWith('conflicts-with:'));
  for (const r of conflicts) {
    const conflictId = r.slice('conflicts-with:'.length);
    if (active.has(conflictId)) {
      trace.push({ rule: 'conflicts', ok: false, conflictWith: conflictId });
      return decide(DECISIONS.CONFLICT_DETECTED, `CONFLICT_${conflictId}`);
    }
  }
  trace.push({ rule: 'conflicts', ok: true });

  return decide(DECISIONS.ELIGIBLE, 'OK');
};

export { DECISIONS, evaluateEligibility };
